import React, { useState } from "react";
import { Modal, Form, Input, Button, Select, Upload, FormInstance, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import type { UploadProps, UploadFile } from "antd";

// Mirrors the backend rules (nosey-mrss-feed services/snowflake/segments/source-url.ts):
// the batch processor downloads from S3 ONLY (bucket = first hostname label, key = path),
// so the only URLs that work are https://<bucket>.s3[.<region>].amazonaws.com/<key> and
// s3://<bucket>/<key>. Drive/Dropbox/path-style/http links submit a job that fails later.
const S3_SOURCE_URL_RE =
	/^(?:https:\/\/[a-z0-9][a-z0-9.-]{1,61}[a-z0-9]\.s3(?:[.-][a-z0-9-]+)?\.amazonaws\.com\/.+|s3:\/\/[a-z0-9][a-z0-9.-]{1,61}[a-z0-9]\/.+)$/i;
const S3_SOURCE_URL_HELP =
	"Must be an S3 URL — https://<bucket>.s3.amazonaws.com/<key> or s3://<bucket>/<key>. Google Drive / Dropbox / other links cannot be downloaded by the processor; upload the file to S3 first (e.g. marathon-streaming).";
// The identifier becomes part of the S3 key (<assetIdentifier>_<file>_normalized.ts) and the
// segment_identifier (single_<assetIdentifier>_1): keep it key-safe.
const ASSET_IDENTIFIER_RE = /^[A-Za-z0-9][A-Za-z0-9 ._()-]*$/;
const ASSET_IDENTIFIER_HELP = "Letters, digits, spaces, . _ ( ) - only (no / ? # % + &, no leading/trailing spaces).";
// nginx client_max_body_size on the API is 100M; bigger files must come in via S3 URL.
const MAX_UPLOAD_BYTES = 100 * 1024 * 1024;

interface AddSegmentFormValues {
	title: string;
	assetIdentifier: string;
	mediaType: "ad" | "media" | "episode" | "filler";
	videoUrl?: string;
	file?: File;
}

interface AddSegmentModalProps {
	form: FormInstance;
	visible: boolean;
	onClose: () => void;
	onSubmit: (values: AddSegmentFormValues) => void;
}

const AddSegmentModal: React.FC<AddSegmentModalProps> = ({ form, visible, onClose, onSubmit }) => {
	const [fileList, setFileList] = useState<UploadFile[]>([]);

	const handleOk = async () => {
		try {
			const values = await form.validateFields();

			const hasFile = fileList.length > 0;
			const hasUrl = !!values.s3_url;

			if (!hasFile && !hasUrl) {
				message.error("Please provide either a video URL or upload a file.");
				return;
			}
			if (hasFile && hasUrl) {
				message.error("Please provide only one of: video URL or file upload — not both.");
				return;
			}

			onSubmit({
				title: values.title,
				assetIdentifier: values.asset_identifier,
				mediaType: values.media_type,
				videoUrl: hasUrl ? values.s3_url : undefined,
				file: hasFile ? (fileList[0].originFileObj as File) : undefined,
			});
			handleClose();
		} catch (error) {
			console.error("Validation Failed:", error);
		}
	};

	const handleClose = () => {
		form.resetFields();
		setFileList([]);
		onClose();
	};

	const uploadProps: UploadProps = {
		fileList,
		beforeUpload: (file) => {
			if (file.size > MAX_UPLOAD_BYTES) {
				message.error(
					`File is ${(file.size / 1024 / 1024).toFixed(0)} MB — the upload limit is 100 MB. Put it in S3 (e.g. marathon-streaming) and paste the S3 URL instead.`
				);
				return Upload.LIST_IGNORE;
			}
			return false; // keep in the list, upload happens on Save
		},
		onChange: ({ fileList: newFileList }) => {
			setFileList(newFileList.slice(-1));
		},
		onRemove: () => setFileList([]),
		maxCount: 1,
	};

	return (
		<Modal
			title="Add Segment"
			open={visible}
			onCancel={handleClose}
			footer={[
				<Button key="cancel" onClick={handleClose}>
					Cancel
				</Button>,
				<Button key="submit" type="primary" onClick={handleOk}>
					Save
				</Button>,
			]}
		>
			<Form form={form} layout="vertical">
				<Form.Item
					label="Title"
					name="title"
					rules={[{ required: true, message: "Please enter the title" }]}
				>
					<Input placeholder="Enter title" />
				</Form.Item>

				<Form.Item
					label="Asset Identifier"
					name="asset_identifier"
					rules={[
						{ required: true, message: "Please enter the asset identifier" },
						{ pattern: ASSET_IDENTIFIER_RE, message: ASSET_IDENTIFIER_HELP },
					]}
					extra="Becomes part of the S3 key and the segment identifier — use a slug like escandalos_filler_maury_60s_01."
				>
					<Input placeholder="Enter asset identifier" />
				</Form.Item>

				<Form.Item
					label="Asset URL"
					name="s3_url"
					validateTrigger={["onBlur", "onSubmit"]}
					rules={[
						{
							validator: (_, value) => {
								const v = (value ?? "").trim();
								if (!v) {
									return fileList.length > 0
										? Promise.resolve()
										: Promise.reject(new Error("Enter an S3 URL or upload a file below."));
								}
								return S3_SOURCE_URL_RE.test(v)
									? Promise.resolve()
									: Promise.reject(new Error(S3_SOURCE_URL_HELP));
							},
						},
					]}
					extra="https://<bucket>.s3.amazonaws.com/<key> or s3://<bucket>/<key> — S3 only."
				>
					<Input placeholder="https://marathon-streaming.s3.amazonaws.com/NoseyEscandalos/your-file.mp4" disabled={fileList.length > 0} />
				</Form.Item>

				<Form.Item label="Or Upload File" extra="Up to 100 MB. Larger files: upload to S3 and paste the S3 URL above.">
					<Upload {...uploadProps}>
						<Button icon={<UploadOutlined />} disabled={!!form.getFieldValue("s3_url")}>
							Select File
						</Button>
					</Upload>
				</Form.Item>

				<Form.Item
					label="Media Type"
					name="media_type"
					rules={[{ required: true, message: "Please select a media type" }]}
				>
					<Select placeholder="Select media type">
						<Select.Option value="ad">Ad</Select.Option>
						<Select.Option value="media">Media</Select.Option>
						<Select.Option value="episode">Episode</Select.Option>
						{/* Filler: short promos / house pieces the scheduler's "Other Filler" /
						    "Paid Programming Filler" pickers use to fill a day's tail. */}
						<Select.Option value="filler">Filler</Select.Option>
					</Select>
				</Form.Item>
			</Form>
		</Modal>
	);
};

export default AddSegmentModal;
